/**
 * Boss event overlay UI
 * Full-screen popup that appears when a boss spawns
 */

import { getBossDef } from '../game/data/boss.js';
import { formatNumber } from '../utils/format.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { dispatch, getState, showToast } from './renderer.js';
import { AdManager, getAdRewards } from '../monetization/index.js';
import { showBossDefeatPopup, showBossEscapePopup } from './bossPopups.js';

let overlayEl: HTMLElement | null = null;
let updateInterval: ReturnType<typeof setInterval> | null = null;

/** Check if boss overlay is currently visible */
export function isBossOverlayVisible(): boolean {
  return overlayEl !== null;
}

/** Show boss fight overlay */
export function showBossOverlay(): void {
  if (overlayEl) return;
  const state = getState();
  const boss = state.boss.current;
  if (!boss) return;

  const def = getBossDef(boss.bossId);
  if (!def) return;

  overlayEl = document.createElement('div');
  overlayEl.className = 'boss-overlay';
  overlayEl.innerHTML = buildFightHTML(def.nameKey, def.imagePath);
  document.body.appendChild(overlayEl);

  bindTapEvents();
  startUpdater();
  playSfx('achievement');
}

/** Remove boss overlay from DOM */
export function closeBossOverlay(): void {
  stopUpdater();
  if (overlayEl) {
    overlayEl.remove();
    overlayEl = null;
  }
}

/** Build boss fight HTML */
function buildFightHTML(nameKey: string, imagePath: string): string {
  return `
    <div class="boss-container">
      <div class="boss-header">${t('boss_alert')}</div>
      <div class="boss-image-area">
        <img src="${imagePath}" alt="" class="boss-img" id="boss-img"
             onerror="this.style.display='none'" />
      </div>
      <div class="boss-name" id="boss-name">${t(nameKey)}</div>
      <div class="boss-phase-badge" id="boss-phase-badge" style="display:none">
        ${t('boss_phase2')}
      </div>
      <div class="boss-hp-bar-container">
        <div class="boss-hp-bar" id="boss-hp-bar"></div>
        <div class="boss-hp-text" id="boss-hp-text"></div>
      </div>
      <div class="boss-timer" id="boss-timer"></div>
      <div class="boss-streak" id="boss-streak"></div>
      <button class="boss-tap-btn" id="boss-tap-btn">${t('boss_tap_attack')}</button>
      <div class="boss-ad-row">
        <button class="ad-btn boss-ad-btn" id="boss-ad-double-dmg">${t('boss_double_damage')}</button>
      </div>
    </div>
  `;
}

/** Bind tap and ad button events */
function bindTapEvents(): void {
  document.getElementById('boss-tap-btn')?.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    dispatch({ type: 'TAP_BOSS' });
    playSfx('tap');
    checkDefeated();
  });

  document.getElementById('boss-ad-double-dmg')?.addEventListener('click', () => {
    const rewards = getAdRewards();
    AdManager.showRewardedAd(
      rewards.INCOME_X5,
      () => {
        dispatch({ type: 'AD_BOSS_DOUBLE_DAMAGE' });
        showToast(t('boss_double_damage_active'));
        playSfx('boost');
      },
      () => { /* cancelled */ },
    );
  });
}

/** Start periodic HP/timer updater */
function startUpdater(): void {
  if (updateInterval) clearInterval(updateInterval);
  updateInterval = setInterval(() => {
    const state = getState();
    const boss = state.boss.current;
    if (!boss) { closeBossOverlay(); return; }
    updateDisplay(boss);
    checkExpired(boss);
  }, 100);
}

/** Stop periodic updater */
function stopUpdater(): void {
  if (updateInterval) { clearInterval(updateInterval); updateInterval = null; }
}

/** Update HP bar, timer, phase visuals */
function updateDisplay(boss: NonNullable<ReturnType<typeof getState>['boss']['current']>): void {
  const def = getBossDef(boss.bossId);
  if (!def) return;

  const now = Date.now();
  const hpPct = Math.max(0, (boss.currentHp / boss.maxHp) * 100);
  const remaining = Math.max(0, Math.ceil((boss.expiresAt - now) / 1000));

  const hpBar = document.getElementById('boss-hp-bar');
  if (hpBar) {
    hpBar.style.width = `${hpPct}%`;
    hpBar.className = `boss-hp-bar ${hpPct < 30 ? 'low' : hpPct < 60 ? 'mid' : ''}`;
  }

  const hpText = document.getElementById('boss-hp-text');
  if (hpText) hpText.textContent = `${formatNumber(boss.currentHp)} / ${formatNumber(boss.maxHp)}`;

  const timerEl = document.getElementById('boss-timer');
  if (timerEl) {
    timerEl.textContent = `${t('boss_time_remaining')}: ${remaining}s`;
    if (remaining <= 5) timerEl.classList.add('critical');
  }

  if (boss.phase === 2) {
    const badge = document.getElementById('boss-phase-badge');
    if (badge) badge.style.display = '';
    const img = document.getElementById('boss-img') as HTMLImageElement | null;
    if (img && !img.src.includes('rage')) img.src = def.phase2ImagePath;
  }

  const streakEl = document.getElementById('boss-streak');
  const state = getState();
  if (streakEl && state.boss.bossKillStreak > 0) {
    streakEl.textContent = t('boss_streak').replace('{n}', String(state.boss.bossKillStreak));
  }

  const adBtn = document.getElementById('boss-ad-double-dmg');
  if (adBtn && boss.doubleDamageUntil > 0 && now < boss.doubleDamageUntil) {
    adBtn.textContent = t('boss_double_damage_active');
    adBtn.classList.add('active');
  }
}

/** Check if boss HP reached 0 */
function checkDefeated(): void {
  const boss = getState().boss.current;
  if (!boss || boss.currentHp > 0) return;
  stopUpdater();
  if (overlayEl) showBossDefeatPopup(overlayEl, closeBossOverlay, showBossOverlay);
}

/** Check if boss time expired */
function checkExpired(boss: NonNullable<ReturnType<typeof getState>['boss']['current']>): void {
  if (boss.currentHp <= 0) {
    stopUpdater();
    if (overlayEl) showBossDefeatPopup(overlayEl, closeBossOverlay, showBossOverlay);
    return;
  }
  if (Date.now() > boss.expiresAt) {
    stopUpdater();
    if (overlayEl) showBossEscapePopup(overlayEl, closeBossOverlay, showBossOverlay);
  }
}
